import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { DateTime, Duration } from 'luxon';
import { useSettingsContext } from '@mono/platmap-settings-context';
import { toHash } from '@mono/to-hash';
import { useWxtilesV3 } from '@mono/use-wxtiles-v3';
import { IStage } from '@mono/types';
import { usePlatStageContext } from '@mono/use-plat-stage';
import { isWXTilesV3Layer } from './layerTypeUtils';
import { roundToNearestTime } from '@mono/round-time';

export const TimeContext = createContext<{
    selectedTime?: string;
    setSelectedTime: React.Dispatch<React.SetStateAction<string | undefined>>;
    timespan?: string;
    now: string;
    timePoints?: string[];
    isLoading: {
        wxtiles?: boolean;
        wxtilesV3?: boolean;
        lightning?: boolean;
    };
    setIsLoading: React.Dispatch<
        React.SetStateAction<{
            wxtiles?: boolean;
            wxtilesV3?: boolean;
            lightning?: boolean;
        }>
    >;
    updateNow: () => void;
}>({
    setSelectedTime: () => {},
    now: DateTime.utc().setZone('utc').toISO(),
    isLoading: {
        wxtiles: false,
        wxtilesV3: false,
        lightning: false,
    },
    setIsLoading: () => {},
    updateNow: () => {},
});

interface TimeContextProviderProps {
    stage: IStage;
    children: React.ReactNode;
}

export const TimeContextProvider = ({ children }: TimeContextProviderProps) => {
    const { stage } = usePlatStageContext();
    const [selectedTime, setSelectedTime] = useState<string>();
    const { settings } = useSettingsContext();

    const [isLoading, setIsLoading] = useState<{
        wxtiles?: boolean;
        wxtilesV3?: boolean;
        lightning?: boolean;
    }>({
        wxtiles: undefined,
        wxtilesV3: undefined,
        lightning: undefined,
    });

    const [now, setNow] = useState(DateTime.utc().setZone('utc').toISO());

    const wxtilesV3DatasetIds = settings.layers.filter(isWXTilesV3Layer).map((layer) => layer.state.datasetId);

    const { wxDatasetManagers } = useWxtilesV3({ datasetsToExpand: wxtilesV3DatasetIds, stage, refreshTime: 1000 * 60 });

    useEffect(() => {
        const intervalHook = setInterval(
            () => {
                setNow(DateTime.utc().setZone('utc').toISO());
            },
            settings.timeControl.type === 'livemode' ? 1000 : 1000 * 60
        );

        return () => {
            clearInterval(intervalHook);
        };
    }, [settings.timeControl.type]);

    useEffect(() => {
        if (settings.timeControl.type === 'timeoffset') {
            setSelectedTime(DateTime.fromISO(now).setZone('utc').plus(Duration.fromISO(settings.timeControl.offset)).toISO());
            return;
        }
        if (settings.timeControl.type === 'livemode') {
            setSelectedTime(now);
            return;
        }
    }, [toHash(settings.timeControl), now]);

    const timePoints = useMemo(() => {
        if (settings.timeControl.type !== 'timespan') return;
        const markStepToRoundTo = settings.syncTimelineTo ? Duration.fromObject({ minutes: 5 }) : Duration.fromISO(settings.timeControl.markStep);

        const [startDuration, endDuration] = settings.timeControl.timespan.split('--').map((time) => Duration.fromISO(time));

        const startTime = roundToNearestTime({
            time: DateTime.fromISO(now).plus(startDuration).toISO(),
            roundTo: { unit: 'minute', value: markStepToRoundTo.as('minutes') },
            operation: 'floor',
            timezone: settings.timezone,
        });
        const endTime = roundToNearestTime({
            time: DateTime.fromISO(now).plus(endDuration).toISO(),
            roundTo: { unit: 'minute', value: markStepToRoundTo.as('minutes') },
            operation: 'ceil',
            timezone: settings.timezone,
        });

        const timespanInSeconds = DateTime.fromISO(endTime).diff(DateTime.fromISO(startTime)).as('seconds');
        const markStepInSeconds = Duration.fromISO(settings.syncTimelineTo === undefined ? settings.timeControl.markStep : 'PT1M').as('seconds');

        const numberOfMarks = Math.floor(timespanInSeconds / markStepInSeconds);
        const firstTimePoint = DateTime.fromISO(startTime);

        if (settings.syncTimelineTo !== undefined) {
            const layerToSyncWith = settings.layers.find((l) => l.id === settings.syncTimelineTo);
            if (layerToSyncWith?.state.type !== 'wxtilesv3') return;
            const syncedTimes = wxDatasetManagers[layerToSyncWith.state.datasetId]?.getAllTimes();
            if (!syncedTimes) return;
            const syncedTimePoints = syncedTimes.map((time) => DateTime.fromISO(time).setZone(settings.timezone).toISO());

            const validTimePoints = syncedTimePoints.filter((timePoint) => {
                return timePoint >= startTime && timePoint <= endTime;
            });

            return validTimePoints;
        }

        const points = Array.from({ length: numberOfMarks + 1 }).map((_, i) => {
            const stepTime = firstTimePoint.plus({ seconds: i * markStepInSeconds });
            return stepTime.toISO();
        });

        return points;
    }, [toHash(settings.timeControl), toHash(isLoading), now, settings.syncTimelineTo, Object.keys(wxDatasetManagers).join(), toHash(settings.layers)]);

    const timespan = useMemo(() => {
        if (!timePoints) return;

        return [timePoints[0], timePoints[timePoints.length - 1]].join('--');
    }, [toHash(timePoints)]);

    return (
        <TimeContext.Provider
            value={{
                selectedTime,
                setSelectedTime,
                timespan,
                now,
                timePoints,
                isLoading,
                setIsLoading,
                updateNow: () => setNow(DateTime.utc().toISO()),
            }}
        >
            {children}
        </TimeContext.Provider>
    );
};

export const useTimeContext = () => useContext(TimeContext);
